import { z } from 'zod';

import type {
  RegistrationCode,
  RegistrationCodeDiscountType,
  RegistrationCodeDurationUnit,
  RegistrationCodePayload,
} from '@Types/registrationCode';

export const CODE_PATTERN = /^[A-Z0-9_-]+$/;

/** Toast's minimum price; the form's "Free" option stores and charges it. */
export const FREE_PRICE_OVERRIDE = 0.01;

/** Stored window that stands for "no end". */
export const FOREVER_WINDOW = { amount: 200, unit: 'YEAR' } as const;

const MAX_PERCENTAGE = 100;
const MIN_WINDOW_AMOUNT = 1;
const CENTS_PER_DOLLAR = 100;

export const DISCOUNT_KINDS = [
  'NONE',
  'PERCENT',
  'AMOUNT_OFF',
  'SET_PRICE',
  'FREE',
] as const;

export type DiscountKind = (typeof DISCOUNT_KINDS)[number];

export const DISCOUNT_KIND_LABELS: Record<DiscountKind, string> = {
  NONE: 'None',
  PERCENT: '% off',
  AMOUNT_OFF: '$ off',
  SET_PRICE: 'Set price',
  FREE: 'Free',
};

export const WINDOW_KINDS = ['FOREVER', 'LIMITED'] as const;

export type WindowKind = (typeof WINDOW_KINDS)[number];

export const WINDOW_KIND_LABELS: Record<WindowKind, string> = {
  FOREVER: 'Forever',
  LIMITED: 'For a set time after joining',
};

export const WINDOW_UNITS = ['DAY', 'MONTH', 'YEAR'] as const;

export type WindowUnit = (typeof WINDOW_UNITS)[number];

export const WINDOW_UNIT_LABELS: Record<WindowUnit, string> = {
  DAY: 'days',
  MONTH: 'months',
  YEAR: 'years',
};

/** Parses a typed money/percentage value; blank or non-numeric gives `null`. */
export const parseAmount = (value: string | undefined): number | null => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundToCents = (value: number) =>
  Math.round(value * CENTS_PER_DOLLAR) / CENTS_PER_DOLLAR;

export const needsAmount = (kind: DiscountKind) =>
  kind === 'PERCENT' || kind === 'AMOUNT_OFF' || kind === 'SET_PRICE';

const discountAmountIssue = (kind: DiscountKind, amount: string) => {
  if (!needsAmount(kind)) return null;
  const parsed = parseAmount(amount);
  if (parsed === null) return 'Enter an amount.';
  if (kind === 'PERCENT' && (parsed <= 0 || parsed > MAX_PERCENTAGE)) {
    return `Enter a percentage above 0 and up to ${MAX_PERCENTAGE}.`;
  }
  if (parsed <= 0) return 'Enter an amount above $0.';
  return null;
};

const windowAmountIssue = (windowKind: WindowKind, windowAmount: string) => {
  if (windowKind !== 'LIMITED') return null;
  const parsed = parseAmount(windowAmount);
  if (parsed === null || !Number.isInteger(parsed) || parsed < MIN_WINDOW_AMOUNT) {
    return 'Enter a whole number of at least 1.';
  }
  return null;
};

const CreditAmountSchema = z
  .string()
  .refine((amount) => !amount.trim() || parseAmount(amount) !== null, {
    message: 'Enter a dollar amount.',
  });

const SignupDiscountSchema = z
  .object({
    kind: z.enum(DISCOUNT_KINDS),
    amount: z.string(),
  })
  .superRefine((value, ctx) => {
    const issue = discountAmountIssue(value.kind, value.amount);
    if (issue) ctx.addIssue({ code: 'custom', path: ['amount'], message: issue });
  });

const RenewalDiscountSchema = z
  .object({
    kind: z.enum(DISCOUNT_KINDS),
    amount: z.string(),
    windowKind: z.enum(WINDOW_KINDS),
    windowAmount: z.string(),
    windowUnit: z.enum(WINDOW_UNITS),
  })
  .superRefine((value, ctx) => {
    const amountIssue = discountAmountIssue(value.kind, value.amount);
    if (amountIssue) {
      ctx.addIssue({ code: 'custom', path: ['amount'], message: amountIssue });
    }
    if (value.kind === 'NONE') return;
    const windowIssue = windowAmountIssue(value.windowKind, value.windowAmount);
    if (windowIssue) {
      ctx.addIssue({
        code: 'custom',
        path: ['windowAmount'],
        message: windowIssue,
      });
    }
  });

const SignupCreditSchema = z.object({ amount: CreditAmountSchema });

const RenewalCreditSchema = z
  .object({
    amount: CreditAmountSchema,
    windowKind: z.enum(WINDOW_KINDS),
    windowAmount: z.string(),
    windowUnit: z.enum(WINDOW_UNITS),
  })
  .superRefine((value, ctx) => {
    if (!parseAmount(value.amount)) return;
    const windowIssue = windowAmountIssue(value.windowKind, value.windowAmount);
    if (windowIssue) {
      ctx.addIssue({
        code: 'custom',
        path: ['windowAmount'],
        message: windowIssue,
      });
    }
  });

const discountFields = {
  promoDetails: z.array(z.object({ value: z.string() })),
  allowedSubTierIds: z
    .array(z.string())
    .min(1, { message: 'Select at least one tier.' }),
  signupFee: SignupDiscountSchema,
  signupWallet: SignupDiscountSchema,
  signupCredit: SignupCreditSchema,
  renewalFee: RenewalDiscountSchema,
  renewalWallet: RenewalDiscountSchema,
  renewalCredit: RenewalCreditSchema,
};

export const InviteCodeFormSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, { message: 'Code is required.' })
    .regex(CODE_PATTERN, {
      message: 'Use only letters, numbers, dashes and underscores.',
    }),
  ...discountFields,
});

/** Preview runs without a valid code; the code only matters for Toast name matching. */
export const InviteCodePreviewSchema = z.object({
  code: z.string(),
  ...discountFields,
});

export type InviteCodeFormValues = z.input<typeof InviteCodeFormSchema>;

type SignupDiscountValues = InviteCodeFormValues['signupFee'];
type RenewalDiscountValues = InviteCodeFormValues['renewalFee'];
type RenewalCreditValues = InviteCodeFormValues['renewalCredit'];
type WindowValues = Pick<
  RenewalDiscountValues,
  'windowKind' | 'windowAmount' | 'windowUnit'
>;

const DEFAULT_WINDOW: WindowValues = {
  windowKind: 'FOREVER',
  windowAmount: '',
  windowUnit: 'YEAR',
};

const NO_DISCOUNT: SignupDiscountValues = { kind: 'NONE', amount: '' };

const toDiscountValues = (
  amount: number | string,
  type: RegistrationCodeDiscountType,
): SignupDiscountValues => {
  const parsed = Number(amount);
  if (!parsed) return NO_DISCOUNT;
  if (type === 'PRICEOVERRIDE') {
    return parsed === FREE_PRICE_OVERRIDE
      ? { kind: 'FREE', amount: '' }
      : { kind: 'SET_PRICE', amount: String(parsed) };
  }
  if (type === 'PERCENTAGE') return { kind: 'PERCENT', amount: String(parsed) };
  return { kind: 'AMOUNT_OFF', amount: String(parsed) };
};

const isWindowUnit = (unit: string): unit is WindowUnit =>
  (WINDOW_UNITS as readonly string[]).includes(unit);

const toWindowValues = (
  amount: number,
  unit: RegistrationCodeDurationUnit,
): WindowValues => {
  const isForever =
    unit === 'LIFETIME' ||
    (amount >= FOREVER_WINDOW.amount && unit === FOREVER_WINDOW.unit);
  if (isForever || amount < MIN_WINDOW_AMOUNT || !isWindowUnit(unit)) {
    return DEFAULT_WINDOW;
  }
  return { windowKind: 'LIMITED', windowAmount: String(amount), windowUnit: unit };
};

export const toInviteCodeFormValues = (
  inviteCode?: RegistrationCode | null,
): InviteCodeFormValues => {
  if (!inviteCode) {
    return {
      code: '',
      promoDetails: [{ value: '' }],
      allowedSubTierIds: [],
      signupFee: NO_DISCOUNT,
      signupWallet: NO_DISCOUNT,
      signupCredit: { amount: '' },
      renewalFee: { ...NO_DISCOUNT, ...DEFAULT_WINDOW },
      renewalWallet: { ...NO_DISCOUNT, ...DEFAULT_WINDOW },
      renewalCredit: { amount: '', ...DEFAULT_WINDOW },
    };
  }

  const initialAdjustment = Number(inviteCode.initialAdjustmentAmount);
  const subsequentAdjustment = Number(inviteCode.subsequentAdjustmentAmount);

  return {
    code: inviteCode.code ?? '',
    promoDetails: inviteCode.promoDetails?.length
      ? inviteCode.promoDetails.map((value) => ({ value }))
      : [{ value: '' }],
    allowedSubTierIds: inviteCode.allowedSubTierIds ?? [],
    signupFee: toDiscountValues(
      inviteCode.initialSubscriptionDiscountAmount,
      inviteCode.initialSubscriptionDiscountType,
    ),
    signupWallet: toDiscountValues(
      inviteCode.initialWalletDiscountAmount,
      inviteCode.initialWalletDiscountType,
    ),
    signupCredit: {
      amount: initialAdjustment ? String(initialAdjustment) : '',
    },
    renewalFee: {
      ...toDiscountValues(
        inviteCode.subsequentSubscriptionDiscountAmount,
        inviteCode.subsequentSubscriptionDiscountType,
      ),
      ...toWindowValues(
        inviteCode.subsequentSubscriptionDiscountDurationAmount,
        inviteCode.subsequentSubscriptionDiscountDurationUnit,
      ),
    },
    renewalWallet: {
      ...toDiscountValues(
        inviteCode.subsequentWalletDiscountAmount,
        inviteCode.subsequentWalletDiscountType,
      ),
      ...toWindowValues(
        inviteCode.subsequentWalletDiscountDurationAmount,
        inviteCode.subsequentWalletDiscountDurationUnit,
      ),
    },
    renewalCredit: {
      amount: subsequentAdjustment ? String(subsequentAdjustment) : '',
      ...toWindowValues(
        inviteCode.subsequentAdjustmentDurationAmount,
        inviteCode.subsequentAdjustmentDurationUnit,
      ),
    },
  };
};

const toDiscountFields = ({
  kind,
  amount,
}: SignupDiscountValues): {
  amount: number;
  type: RegistrationCodeDiscountType;
} => {
  const parsed = roundToCents(parseAmount(amount) ?? 0);
  switch (kind) {
    case 'PERCENT':
      return { amount: parsed, type: 'PERCENTAGE' };
    case 'AMOUNT_OFF':
      return { amount: parsed, type: 'FLATAMOUNT' };
    case 'SET_PRICE':
      return { amount: parsed, type: 'PRICEOVERRIDE' };
    case 'FREE':
      return { amount: FREE_PRICE_OVERRIDE, type: 'PRICEOVERRIDE' };
    case 'NONE':
    default:
      return { amount: 0, type: 'FLATAMOUNT' };
  }
};

const toWindowFields = (
  applies: boolean,
  { windowKind, windowAmount, windowUnit }: WindowValues,
): { amount: number; unit: RegistrationCodeDurationUnit } => {
  if (!applies || windowKind === 'FOREVER') return { ...FOREVER_WINDOW };
  return { amount: parseAmount(windowAmount) ?? 0, unit: windowUnit };
};

const toRenewalCreditFields = (renewalCredit: RenewalCreditValues) => {
  const amount = roundToCents(parseAmount(renewalCredit.amount) ?? 0);
  return { amount, window: toWindowFields(amount !== 0, renewalCredit) };
};

export const toInviteCodePayload = (
  values: InviteCodeFormValues,
): RegistrationCodePayload => {
  const signupFee = toDiscountFields(values.signupFee);
  const signupWallet = toDiscountFields(values.signupWallet);
  const renewalFee = toDiscountFields(values.renewalFee);
  const renewalFeeWindow = toWindowFields(
    values.renewalFee.kind !== 'NONE',
    values.renewalFee,
  );
  const renewalWallet = toDiscountFields(values.renewalWallet);
  const renewalWalletWindow = toWindowFields(
    values.renewalWallet.kind !== 'NONE',
    values.renewalWallet,
  );
  const renewalCredit = toRenewalCreditFields(values.renewalCredit);

  return {
    code: values.code.trim().toUpperCase(),
    promoDetails: values.promoDetails
      .map(({ value }) => value.trim())
      .filter(Boolean),
    allowedSubTierIds: values.allowedSubTierIds,
    initialSubscriptionDiscountAmount: signupFee.amount,
    initialSubscriptionDiscountType: signupFee.type,
    subsequentSubscriptionDiscountAmount: renewalFee.amount,
    subsequentSubscriptionDiscountType: renewalFee.type,
    subsequentSubscriptionDiscountDurationAmount: renewalFeeWindow.amount,
    subsequentSubscriptionDiscountDurationUnit: renewalFeeWindow.unit,
    initialWalletDiscountAmount: signupWallet.amount,
    initialWalletDiscountType: signupWallet.type,
    subsequentWalletDiscountAmount: renewalWallet.amount,
    subsequentWalletDiscountType: renewalWallet.type,
    subsequentWalletDiscountDurationAmount: renewalWalletWindow.amount,
    subsequentWalletDiscountDurationUnit: renewalWalletWindow.unit,
    initialAdjustmentAmount: roundToCents(
      parseAmount(values.signupCredit.amount) ?? 0,
    ),
    subsequentAdjustmentAmount: renewalCredit.amount,
    subsequentAdjustmentDurationAmount: renewalCredit.window.amount,
    subsequentAdjustmentDurationUnit: renewalCredit.window.unit,
  };
};

/** Fields of `next` whose value differs from `baseline`. */
export const pickChangedFields = (
  next: RegistrationCodePayload,
  baseline: RegistrationCodePayload,
): Partial<RegistrationCodePayload> => {
  const keys = Object.keys(next) as Array<keyof RegistrationCodePayload>;
  return keys.reduce<Partial<RegistrationCodePayload>>((changes, key) => {
    if (JSON.stringify(next[key]) !== JSON.stringify(baseline[key])) {
      return { ...changes, [key]: next[key] };
    }
    return changes;
  }, {});
};

const RENEWAL_FIELD_PREFIX = 'subsequent';

export const touchesRenewalFields = (
  changes: Partial<RegistrationCodePayload>,
) => Object.keys(changes).some((key) => key.startsWith(RENEWAL_FIELD_PREFIX));
